/**
 * usePacingData — Data-fetching hook for the Pacing History page.
 *
 * Loads monthly trend data (which now includes budget and pacing %)
 * and current MTD metrics for a given portco.
 */

import { useEffect, useState } from "react";
import {
  getPortcoMonthlyTrend,
  getPortcoMetrics,
  MonthlyTrendPoint,
  PortcoMetrics,
} from "../utils/PowerBIApiCall";

export interface PacingData {
  monthlyTrend: MonthlyTrendPoint[] | null;
  metrics: PortcoMetrics | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetch monthly trend + current metrics for a portco.
 *
 * @param portcoId  — slug from URL params
 * @param enabled   — gate fetching (e.g. wait for MSAL interaction). Defaults to true.
 * @param onAuthError — optional callback for InteractionRequiredAuthError in live mode
 */
export function usePacingData(
  portcoId: string | undefined,
  enabled: boolean = true,
  onAuthError?: (error: unknown) => void,
): PacingData {
  const [monthlyTrend, setMonthlyTrend] = useState<MonthlyTrendPoint[] | null>(
    null,
  );
  const [metrics, setMetrics] = useState<PortcoMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!portcoId || !enabled) return;

    let cancelled = false;

    async function loadData() {
      try {
        setLoading(true);
        setError(null);

        const [trend, metricsData] = await Promise.all([
          getPortcoMonthlyTrend(portcoId!),
          getPortcoMetrics(portcoId!),
        ]);

        if (cancelled) return;
        setMonthlyTrend(trend);
        setMetrics(metricsData);
      } catch (e) {
        if (cancelled) return;

        if (onAuthError) {
          const { InteractionRequiredAuthError } =
            await import("@azure/msal-browser");
          if (e instanceof InteractionRequiredAuthError) {
            onAuthError(e);
            return;
          }
        }

        setError(e instanceof Error ? e.message : "Failed to load pacing data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => {
      cancelled = true;
    };
  }, [portcoId, enabled]);

  return { monthlyTrend, metrics, loading, error };
}
